import { Selector } from '@/state/useContainerWizzardSteps'

import query from './APIServicesV2'
import APIServices from './APIServices'

interface Containers {
  id: number
  status: string
  createdAt: string
  updatedAt: string
  operationNumber: number
  isReturned: false
  totalItems: number
}

interface ContainerDetailsResponse {
  id: number
  name: string
  status: string
  operationNumber: number
  createdAt: string
  updatedAt: string
  isReturned: boolean
  observations: string
  isDispatched?: boolean
  trackingNumber: number
  levanteNumber: number
  grossWeight: number
  netWeight: number
  totalItems: number
  isRefrigered: boolean
  temperatureRange: string
  code: string
  sealNumber: string
  isPalleted: boolean
  numberOfPallets: number
  categories: Selector[]
  ownerCompany: Selector | null
  managerCompany: Selector | null
  transporterCompany: Selector | null
  storageCompany: Selector | null
  countryFrom: Selector | null
  origin: Selector | null
  destiny: Selector | null
  vehicle: Selector | null
  transportedBy: { id: number; displayName: string; email?: string } | null
}

interface ContainerLogisticsResponse {
  name: string
  id: number
  isDispatched?: boolean
  vehicle: Selector | null
  transportedBy: { id: number; displayName: string } | null
  storageCompany: Selector | null
  transporterCompany: Selector | null
}

interface ContainerCost {
  registeredPrice?: {
    amount: number
    codeCurrency: string
  }
  observations: string | null
  type: string | null
  fixedCostCategoryId: number | null
}

interface CreateContainerData {
  name: string
  observations?: string
  code?: string
  sealNumber?: string
  isRefrigered?: boolean
  temperatureRange?: string
  categories?: number[]
  listCosts?: ContainerCost[]
  netWeight?: number
  isPalleted?: boolean
  numberOfPallets?: number
  trackingNumber?: number
  ownerCompanyId?: number
  importerCompanyId?: number
  transporterCompanyId?: number
  storageCompanyId?: number
  managerCompanyId?: number
  transportedBy?: number
  countryFromId?: number
  originId?: number
  destinyId?: number
}

interface RegisteredPriceDto {
  id?: number
  amount: number
  codeCurrency: string
}

interface ContainerCostParams {
  // When updating a fixed/container cost the backend expects a registeredPrice object
  // matching RegisteredPriceDto (id optional, amount, codeCurrency).
  registeredPrice?: RegisteredPriceDto
  observations?: string
  type?: string
  fixedCostCategoryId?: number
}

interface ContainerProductParams {
  productId?: number
  quantity?: number
  observations?: string
  [key: string]: unknown
}

interface ContainerDocumentParams {
  containerId?: number
  documentType?: string
  documentNumber?: string
  observations?: string
  [key: string]: unknown
}

interface ContainerBatch {
  id?: number
  batchNumber?: string
  quantity?: number
  observations?: string
  [key: string]: unknown
}

interface ContainerBatchParams {
  batchNumber?: string
  quantity?: number
  observations?: string
  [key: string]: unknown
}

export const getContainers = async (options?: {
  withoutCargoFile?: boolean
  search?: string
}) => {
  let url = '/containers'
  const params: Record<string, string> = {}

  if (options?.withoutCargoFile) {
    params.withoutCargoFile = 'true'
  }

  if (options?.search) {
    params.search = options.search
  }

  if (Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params)
    url += '?' + searchParams.toString()
  }

  const response = await query.get(url)
  return response?.data.items as Containers[]
}

export const getContainerDetails = async (id: number) => {
  const response = await query.get(`/containers/${id}/details`)
  return response?.data as ContainerDetailsResponse
}

export const getContainerLogistics = async (id: number) => {
  const response = await query.get(`/containers/${id}/logistics`)
  return response?.data as ContainerLogisticsResponse
}
// Container details update functions
export const updateContainerDetails = async ({
  id,
  data,
}: {
  id: number
  data: {
    status?: string
    name?: string
    operationNumber?: string
    countryFromId?: number
    categories?: number[]
    isReturned?: boolean
    ownerCompanyId?: number | null
    managerCompanyId?: number | null
    observations?: string
  }
}) => {
  const response = await query.patch(`/containers/${id}/details`, data)
  return response.data
}

export const updateContainerAdvancedDetails = async ({
  id,
  data,
}: {
  id: number
  data: {
    originId?: number
    destinyId?: number
    netWeight?: number
    grossWeight?: number
    code?: string
    sealNumber?: string
    isRefrigered?: boolean
    temperatureRange?: string
    trackingNumber?: string
  }
}) => {
  const response = await query.patch(`/containers/${id}/details`, data)
  return response.data
}

export const updateContainerLogistics = async ({
  id,
  data,
}: {
  id: number
  data: {
    storageCompanyId?: number | null
    transporterCompanyId?: number | null
    transportedById?: number | null
    vehicleId?: number | null
  }
}) => {
  const response = await APIServices.patch(
    `/cargo/containers/${id}/logistics`,
    data
  )
  return response.data
}

export const createContainer = async (
  data: CreateContainerData,
  version: 'v1' | 'v2' = 'v2'
) => {
  // Mapear costs si existen en data.listCosts
  const costs = Array.isArray(data.listCosts)
    ? data.listCosts.map((item: ContainerCost) => ({
        registeredPrice: item.registeredPrice ?? {
          amount: 0,
          codeCurrency: 'USD',
        },
        observations: item.observations ?? null,
        type: item.type ?? null,
        fixedCostCategoryId: item.fixedCostCategoryId ?? null,
      }))
    : []

  const containerData: Record<string, unknown> = {
    name: data.name,
    observations: data.observations,
    code: data.code,
    sealNumber: data.sealNumber,
    isRefrigered: data.isRefrigered,
    temperatureRange: data.temperatureRange,
    ...(Array.isArray(data.categories) &&
      data.categories.length > 0 && { categories: data.categories }),
    ...(Array.isArray(costs) && costs.length > 0 && { costs: costs }),
    netWeight: data.netWeight,
    isPalleted: data.isPalleted,
    numberOfPallets: data.numberOfPallets,
    trackingNumber: data.trackingNumber,
    ownerCompanyId: data.ownerCompanyId,
    importerCompanyId: data.importerCompanyId,
    transporterCompanyId: data.transporterCompanyId,
    storageCompanyId: data.storageCompanyId,
    managerCompanyId: data.managerCompanyId,
    transportedById: data.transportedBy,
    countryFromId: data.countryFromId,
    originId: data.originId,
    destinyId: data.destinyId,
  }

  // Filter out null, undefined, and false values for specific boolean properties
  Object.keys(containerData).forEach((key) => {
    if (
      containerData[key] === null ||
      containerData[key] === undefined ||
      (key === 'isRefrigered' && containerData[key] === false) ||
      (key === 'isPalleted' && containerData[key] === false)
    ) {
      containerData[key] = undefined
    }
  })

  // Si version es v2, construye la URL base manualmente usando la variable de entorno V2
  if (version === 'v2') {
    const apiHost = process.env.REACT_APP_API_HOST || ''
    const apiV2 = process.env.REACT_APP_VERSION_API_V2 || '/v2'
    const axios = (await import('axios')).default
    const { store } = await import('../store/root')
    const { constructURL } = await import('../utils/helpers')
    const session = store.getState().session
    const token = session?.key?.token || ''
    const finalURL = constructURL(apiHost, session.processRegion)
    const url = `${finalURL}${apiV2}/containers`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-App-Origin': 'Tecopos-Admin',
    }
    if (token) headers['Authorization'] = `Bearer ${token}`

    const response = await axios.post(url, containerData, { headers })

    // Si la respuesta es exitosa pero no tiene datos útiles
    if (response.status >= 200 && response.status < 300) {
      // Intentar obtener el ID del header Location
      const locationHeader =
        response.headers['location'] || response.headers['Location']
      if (locationHeader) {
        const idMatch = locationHeader.match(/(\d+)$/)
        if (idMatch) {
          const id = parseInt(idMatch[1], 10)
          return { id, data: { id } }
        }
      }

      // Si no hay location header, hacer una consulta adicional para obtener el contenedor más reciente
      try {
        // Usar la API v1 que sabemos que funciona
        const { getContainers } = await import('./containersAPI')
        const containers = await getContainers()

        if (Array.isArray(containers) && containers.length > 0) {
          // Los contenedores deberían venir ordenados por fecha de creación (más reciente primero)
          // Buscar el contenedor con el ID más alto o más reciente
          const latestContainer = containers.reduce(
            (latest: Containers, current: Containers) => {
              return current.id > latest.id ? current : latest
            }
          )

          if (latestContainer.id) {
            return { id: latestContainer.id, data: { id: latestContainer.id } }
          }
        }
      } catch {
        // Como último recurso, intentar con la API v2 sin parámetros de sorting
        try {
          const listResponse = await axios.get(
            `${finalURL}${apiV2}/containers`,
            { headers }
          )

          let containers = []
          if (Array.isArray(listResponse.data)) {
            containers = listResponse.data
          } else if (
            listResponse.data?.data &&
            Array.isArray(listResponse.data.data)
          ) {
            containers = listResponse.data.data
          } else if (
            listResponse.data?.items &&
            Array.isArray(listResponse.data.items)
          ) {
            containers = listResponse.data.items
          }

          if (containers.length > 0) {
            // Buscar el contenedor con el ID más alto
            const latestContainer = containers.reduce(
              (latest: Containers, current: Containers) => {
                return current.id > latest.id ? current : latest
              }
            )

            if (latestContainer.id) {
              return {
                id: latestContainer.id,
                data: { id: latestContainer.id },
              }
            }
          }
        } catch {
          // Error handled silently
        }
      }

      // Como último recurso, retornar error
      throw new Error('No se pudo obtener el ID del contenedor creado')
    }

    return response.data
  }
  // Por defecto usa el sistema de APIServices (que concatena la versión principal)
  const response = await query.post('/containers', containerData)
  return response?.data
}

// Container costs API functions
export const getContainerCosts = async (params: { id: number }) => {
  const response = await query.get(`/containers/${params.id}/costs/`)
  return response.data
}

export const postContainerCosts = async ({
  id,
  params,
}: {
  id: number
  params: ContainerCostParams
}) => await query.post(`/containers/${id}/costs/`, params)

export const patchContainerCosts = async ({
  id,
  params,
}: {
  id: number
  params: ContainerCostParams
}) => await query.patch(`/containers/costs/${id}`, params)

export const deleteContainerCosts = async (id: number) =>
  await query.deleteAPI(`/containers/costs/${id}`)

// Container products API functions
export const getContainerProducts = async (params: { id: number }) => {
  const response = await query.get(`/containers/${params.id}/products`)
  return response.data
}

export const postContainerProducts = async ({
  id,
  params,
}: {
  id: number
  params: ContainerProductParams
}) => await query.post(`/containers/${id}/products`, params)

export const patchContainerProducts = async ({
  id,
  params,
}: {
  id: number
  params: ContainerProductParams
}) => await query.patch(`/containers/${id}/products/${id}`, params)

export const deleteContainerProducts = async (id: number) =>
  await query.deleteAPI(`/containers/batches/${id}`)

// Container documents API functions
export const getContainerDocuments = async (params: { id: number }) => {
  const response = await query.get(`/containers/${params.id}/documents`)
  return response.data
}

export const postContainerDocuments = async ({
  id,
  params,
}: {
  id: number
  params: ContainerDocumentParams
}) => {
  return await query.post(
    `/containers/${id}/documents/`,
    params as Omit<ContainerDocumentParams, 'containerId'>
  )
}

export const patchContainerDocuments = async ({
  id,
  params,
}: {
  id: number
  params: ContainerDocumentParams
}) => await query.patch(`/containers/documents/${id}`, params)

export const deleteContainerDocuments = async (id: number) =>
  await query.deleteAPI(`/containers/documents/${id}`)

export const postContainerBatches = async ({
  id,
  batches,
}: {
  id: number
  batches: ContainerBatch[]
}) => {
  return await query.post(`/containers/${id}/batches`, { batches })
}

export const patchContainerBatch = async ({
  idBatch,
  params,
}: {
  idBatch: number
  params: ContainerBatchParams
}) => {
  return await query.patch(`/containers/batches/${idBatch}`, params)
}

export const getContainerBatches = async (id: number) => {
  const response = await query.get(`/containers/${id}/batches`)
  return response?.data as ContainerBatch[]
}

export const deleteContainerBatch = async (batchId: number) => {
  return await query.deleteAPI(`/containers/batches/${batchId}`)
}

// Container deletion
export const deleteContainer = async (id: number) => {
  const response = await query.deleteAPI(`/containers/${id}`)
  return response.data
}

// Container dispatch
export const dispatchContainer = async (data: {
  containerId: string
  areaId: number
  observations?: string
}) => {
  const payload = {
    areaId: data.areaId,
    observations: data.observations || '',
  }
  const response = await query.post(`/dispatch/${data.containerId}`, payload)
  return response.data
}

// Container ungroup
export const ungroupContainer = async (data: {
  containerId: string
  areaId: number
  // observations?: string // Not used in ungroup endpoint
}) => {
  const payload = {
    areaId: data.areaId,
    products: [],
  }
  const response = await query.post(
    `/containers/${data.containerId}/ungroup`,
    payload
  )
  return response.data
}

// Fetcher functions for SearchableSelect components
export const fetchContainerOrigins = async (search: string) => {
  const params: Record<string, string> = {}
  if (search) params.search = search

  const response = await APIServices.get('/administration/origindestiny', {
    params,
  })
  const items = response.data.items || response.data || []
  return items.map((o: { id: number; name: string }) => ({
    id: o.id,
    label: o.name,
  }))
}
